import React, { useState, useEffect } from 'react';
import { Box, Typography, LinearProgress, Tooltip, Collapse } from '@mui/material';
import { ResponsiveContainer, BarChart, XAxis, YAxis, Bar, Tooltip as RechartsTooltip, CartesianGrid } from 'recharts';
import { useDispatch, useSelector } from 'react-redux';
import {
  getNotificationsAction, deleteNotificationAction,
  getNotificationAction, cleanNotificationAction,
} from '../../redux/actions/notification';
import NotificationEdit from './Edit';
import strings from '../../i18n/language';
import dayjs from 'dayjs';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import NavigateBeforeRoundedIcon from '@mui/icons-material/NavigateBeforeRounded';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';

const BRAND = '#3b95e8';
const GLASS = 'rgba(255,255,255,0.04)';
const BORDER = 'rgba(255,255,255,0.08)';
const MUTED = 'rgba(232,240,254,0.38)';

const ROW_PER_PAGE = 10;

const STATUS_META = {
  planned: { bg: 'rgba(255,193,7,0.14)', color: '#ffc107', label: 'Planificado' },
  done:    { bg: 'rgba(91,191,181,0.14)', color: '#5bbfb5', label: 'Enviado' },
  draft:   { bg: 'rgba(139,159,212,0.12)', color: '#8b9fd4', label: 'Borrador' },
};

const TYPE_META = {
  standard: { bg: 'rgba(59,149,232,0.13)', color: BRAND },
  survey:   { bg: 'rgba(139,159,212,0.13)', color: '#8b9fd4' },
};

const Badge = ({ meta, label }) => (
  <span style={{
    background: meta.bg, color: meta.color,
    borderRadius: 6, padding: '3px 9px',
    fontSize: 11, fontWeight: 600, letterSpacing: 0.3, whiteSpace: 'nowrap',
  }}>
    {label}
  </span>
);

const ColHeader = ({ children, style }) => (
  <th style={{
    textAlign: 'left', fontSize: 11, fontWeight: 600, color: MUTED,
    textTransform: 'uppercase', letterSpacing: 0.6, padding: '14px 16px',
    borderBottom: `1px solid ${BORDER}`, ...style,
  }}>
    {children}
  </th>
);

const Td = ({ children, style }) => (
  <td style={{
    padding: '13px 16px', fontSize: 13, color: 'rgba(232,240,254,0.75)',
    borderBottom: `1px solid rgba(255,255,255,0.04)`, verticalAlign: 'middle', ...style,
  }}>
    {children}
  </td>
);

const ActionBtn = ({ onClick, title, color, disabled, children }) => (
  <Tooltip title={title}>
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: 'none', border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        color: disabled ? 'rgba(255,255,255,0.15)' : MUTED,
        borderRadius: 6, width: 28, height: 28,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        transition: 'color 0.15s, background 0.15s',
      }}
      onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.color = color; e.currentTarget.style.background = `${color}18`; } }}
      onMouseLeave={(e) => { e.currentTarget.style.color = disabled ? 'rgba(255,255,255,0.15)' : MUTED; e.currentTarget.style.background = 'none'; }}
    >
      {children}
    </button>
  </Tooltip>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(13,27,54,0.96)', border: `1px solid ${BORDER}`,
      borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#e8f0fe',
    }}>
      <div style={{ marginBottom: 4, color: MUTED, fontWeight: 600 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: 2, background: p.fill }} />
          <span style={{ fontWeight: 700 }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
};

const SurveyChart = ({ notification }) => {
  const [data, setData] = useState([]);
  useEffect(() => {
    if (notification.type !== 'survey') return;
    const answers = JSON.parse(notification.answers);
    const answerMap = answers.reduce((acc, a) => { acc[a.id] = a.label; return acc; }, {});
    const counts = notification.UserNotifications.reduce((acc, un) => {
      if (un.read && un.answer !== null) {
        const label = answerMap[parseInt(un.answer)];
        acc[label] = (acc[label] || 0) + 1;
      }
      return acc;
    }, {});
    setData(Object.entries(counts).map(([response, count]) => ({ response, count })));
  }, [notification]);

  return (
    <div style={{ padding: '16px 24px', background: 'rgba(255,255,255,0.02)', borderBottom: `1px solid ${BORDER}` }}>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ top: 4, right: 16, left: -16, bottom: 0 }}>
          <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="4 4" vertical={false} />
          <XAxis dataKey="response" tick={{ fill: MUTED, fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fill: MUTED, fontSize: 11 }} axisLine={false} tickLine={false} />
          <RechartsTooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
          <Bar dataKey="count" fill={BRAND} fillOpacity={0.85} radius={[4, 4, 0, 0]} barSize={32} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const NotificationRow = ({ notification, onDelete, onEdit }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const hasSurveyData = notification.type === 'survey' &&
    notification.UserNotifications.filter((un) => un.read).length > 0;
  const readCount = notification.UserNotifications.filter((un) => un.read).length;
  const statusMeta = STATUS_META[notification.status] || { bg: GLASS, color: MUTED, label: notification.status };
  const typeMeta = TYPE_META[notification.type] || { bg: GLASS, color: MUTED };
  const typeLabel = notification.type === 'standard' ? strings.LABEL_NOTIFICATION_STANDARD : strings.LABEL_NOTIFICATION_SURVEY;

  return (
    <>
      <tr
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
        style={{ transition: 'background 0.1s' }}
      >
        <Td style={{ width: 36, paddingRight: 0 }}>
          {hasSurveyData && (
            <button onClick={() => setOpen(!open)} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: MUTED, display: 'flex', alignItems: 'center',
            }}>
              {open
                ? <KeyboardArrowUpRoundedIcon style={{ fontSize: 18 }} />
                : <KeyboardArrowDownRoundedIcon style={{ fontSize: 18 }} />}
            </button>
          )}
        </Td>
        <Td style={{ color: '#e8f0fe', fontWeight: 500 }}>{notification.title}</Td>
        <Td><Badge meta={statusMeta} label={statusMeta.label} /></Td>
        <Td><Badge meta={typeMeta} label={typeLabel} /></Td>
        <Td style={{ color: MUTED, fontSize: 12, whiteSpace: 'nowrap' }}>
          {dayjs(notification.datetime).isValid()
            ? dayjs(notification.datetime).format('LLL')
            : strings.LABEL_NOTIFICATION_NO_DATE}
        </Td>
        <Td style={{ color: MUTED }}>{readCount}</Td>
        <Td>
          <div style={{ display: 'flex', gap: 2 }}>
            {notification.status !== 'done' && (
              <ActionBtn title="Editar" color="#e8f0fe" onClick={() => {
                dispatch(getNotificationAction(notification.id));
                onEdit();
              }}>
                <EditRoundedIcon style={{ fontSize: 15 }} />
              </ActionBtn>
            )}
            <ActionBtn
              title="Eliminar"
              color="#e05c5c"
              disabled={notification.status === 'done'}
              onClick={() => onDelete(notification)}
            >
              <DeleteOutlineRoundedIcon style={{ fontSize: 15 }} />
            </ActionBtn>
          </div>
        </Td>
      </tr>
      {hasSurveyData && (
        <tr>
          <td colSpan={7} style={{ padding: 0, borderBottom: `1px solid rgba(255,255,255,0.04)` }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <SurveyChart notification={notification} />
            </Collapse>
          </td>
        </tr>
      )}
    </>
  );
};

const DeleteDialog = ({ notification, onConfirm, onCancel }) => (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 1300,
    background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    <div style={{
      background: '#0d1b36', border: `1px solid ${BORDER}`,
      borderRadius: 16, padding: '28px 32px', maxWidth: 400, width: '90%',
      boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
    }}>
      <Typography style={{ fontSize: 16, fontWeight: 700, color: '#e8f0fe', marginBottom: 10 }}>
        {strings.LABEL_NOTIFICATION_ALERT_DELETE_TITLE}
      </Typography>
      <Typography style={{ fontSize: 13, color: MUTED, marginBottom: 24 }}>
        {strings.LABEL_NOTIFICATION_ALERT_DELETE_CONTENT}
      </Typography>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button onClick={onCancel} style={{
          background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`,
          borderRadius: 8, color: 'rgba(232,240,254,0.7)',
          fontSize: 13, fontWeight: 500, padding: '8px 16px', cursor: 'pointer',
        }}>
          {strings.LABEL_BUTTON_CANCEL}
        </button>
        <button onClick={onConfirm} style={{
          background: '#e05c5c', border: 'none', borderRadius: 8,
          color: '#fff', fontSize: 13, fontWeight: 600,
          padding: '8px 16px', cursor: 'pointer',
        }}>
          {strings.LABEL_BUTTON_CONFIRM}
        </button>
      </div>
    </div>
  </div>
);

const NotificationList = () => {
  const [page, setPage] = useState(0);
  const [notificationDelete, setNotificationDelete] = useState(null);
  const [openModalEdit, setOpenModalEdit] = useState(false);
  const dispatch = useDispatch();

  const notifications = useSelector((state) => state.notification.notifications);
  const isLoadingGetNotifications = useSelector((state) => state.notification.isLoadingGetNotifications);
  const total = useSelector((state) => state.notification.total);
  const selectedCompany = useSelector((state) => state.login.selectedCompany);

  useEffect(() => {
    dispatch(getNotificationsAction(ROW_PER_PAGE, 0, selectedCompany?.id));
  }, [dispatch, selectedCompany]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    dispatch(getNotificationsAction(ROW_PER_PAGE, newPage * ROW_PER_PAGE, selectedCompany?.id));
  };

  if (!notifications) return null;

  const totalPages = Math.ceil(total / ROW_PER_PAGE);

  return (
    <Box sx={{ width: "100%" }}>
      <div style={{ background: GLASS, border: `1px solid ${BORDER}`, borderRadius: 20, backdropFilter: 'blur(12px)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: `1px solid ${BORDER}` }}>
          <div>
            <Typography style={{ fontSize: 15, fontWeight: 700, color: '#e8f0fe', letterSpacing: 0.2 }}>
              Notificaciones
            </Typography>
            {total > 0 && <Typography style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{total} notificaciones</Typography>}
          </div>
          <button
            onClick={() => { dispatch(cleanNotificationAction()); setOpenModalEdit(true); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: BRAND, color: '#fff', border: 'none', borderRadius: 8,
              padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(59,149,232,0.3)', letterSpacing: 0.2,
            }}>
            <AddRoundedIcon style={{ fontSize: 16 }} />
            Nueva notificación
          </button>
        </div>

        {isLoadingGetNotifications && (
          <LinearProgress sx={{ height: 2, '& .MuiLinearProgress-bar': { background: BRAND } }} />
        )}

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <ColHeader style={{ width: 36 }}></ColHeader>
                <ColHeader>{strings.LABEL_NOTIFICATION_TITLE}</ColHeader>
                <ColHeader>Estado</ColHeader>
                <ColHeader>{strings.LABEL_NOTIFICATION_TYPE}</ColHeader>
                <ColHeader>{strings.LABEL_NOTIFICATION_DATE}</ColHeader>
                <ColHeader>Leídos</ColHeader>
                <ColHeader style={{ width: 80 }}></ColHeader>
              </tr>
            </thead>
            <tbody>
              {!isLoadingGetNotifications && notifications.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: 40, fontSize: 13, color: MUTED }}>
                    {strings.LABEL_NOTIFICATION_NO_NOTIFICATION}
                  </td>
                </tr>
              )}
              {notifications.map((n) => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  onDelete={setNotificationDelete}
                  onEdit={() => setOpenModalEdit(true)}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, padding: '12px 24px' }}>
            <span style={{ fontSize: 12, color: MUTED }}>{page + 1} / {totalPages}</span>
            <button onClick={() => handlePageChange(page - 1)} disabled={page === 0} style={{
              background: 'rgba(255,255,255,0.04)', border: `1px solid ${BORDER}`, borderRadius: 8,
              color: page === 0 ? 'rgba(255,255,255,0.15)' : '#e8f0fe',
              cursor: page === 0 ? 'not-allowed' : 'pointer',
              width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <NavigateBeforeRoundedIcon style={{ fontSize: 18 }} />
            </button>
            <button onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages - 1} style={{
              background: 'rgba(255,255,255,0.04)', border: `1px solid ${BORDER}`, borderRadius: 8,
              color: page >= totalPages - 1 ? 'rgba(255,255,255,0.15)' : '#e8f0fe',
              cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer',
              width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <NavigateNextRoundedIcon style={{ fontSize: 18 }} />
            </button>
          </div>
        )}
      </div>

      <NotificationEdit open={openModalEdit} onClose={() => setOpenModalEdit(false)} />

      {notificationDelete && (
        <DeleteDialog
          notification={notificationDelete}
          onCancel={() => setNotificationDelete(null)}
          onConfirm={() => {
            dispatch(deleteNotificationAction(notificationDelete.id, ROW_PER_PAGE, page, selectedCompany?.id));
            setNotificationDelete(null);
          }}
        />
      )}
    </Box>
  );
};

export default NotificationList;
